import { create } from 'zustand'

export type LicenseBannerStatus =
  | 'valid'
  | 'expiring'
  | 'evaluation'
  | 'grace'
  | 'expired'
  | 'invalid'
  | 'saas-ok'
  | 'saas-unkeyed'
  | 'saas-unverifiable'

interface LicenseState {
  /** Last seen license posture, from the `x-license-status` response header
   *  (or a FILE_LICENSE_* denial code). Null until the first API response —
   *  deliberately NOT persisted: a countdown from a previous session must
   *  never flash before the backend re-evaluates. */
  status: LicenseBannerStatus | null
  /** Countdown accompanying the status: days left (evaluation, expiring)
   *  or hours left (grace). Null for valid/expired/invalid. */
  countdown: number | null
  setFromHeader: (headerValue: string | null | undefined) => void
  setTerminal: (status: 'expired' | 'invalid') => void
  reset: () => void
}

/**
 * Parse the gateway's `x-license-status` header (`valid`, `expiring:N`,
 * `evaluation:N`, `grace:N`, `saas:ok|unkeyed|unverifiable`). Unknown
 * shapes reset to null rather than guessing — a banner must never
 * render from a value it cannot interpret.
 */
export function parseLicenseHeader(headerValue: string | null | undefined): {
  status: LicenseBannerStatus | null
  countdown: number | null
} {
  if (!headerValue) return { status: null, countdown: null }
  const [kind, num] = headerValue.split(':')
  if (kind === 'valid') return { status: 'valid', countdown: null }
  if ((kind === 'expiring' || kind === 'evaluation' || kind === 'grace') && num !== undefined) {
    const n = Number(num)
    if (Number.isInteger(n) && n >= 0) return { status: kind, countdown: n }
  }
  // SaaS postures carry a state word, not a countdown.
  if (kind === 'saas' && (num === 'ok' || num === 'unkeyed' || num === 'unverifiable')) {
    return { status: `saas-${num}`, countdown: null }
  }
  return { status: null, countdown: null }
}

export const useLicenseStore = create<LicenseState>()((set) => ({
  status: null,
  countdown: null,
  setFromHeader: (headerValue) => set(parseLicenseHeader(headerValue)),
  setTerminal: (status) => set({ status, countdown: null }),
  reset: () => set({ status: null, countdown: null }),
}))
